// --- START OF FILE CompletedView.js ---

import React from 'react';
import { Trophy, RotateCcw, BarChart2, Home } from 'lucide-react';

// --- Helper Functions ---
const getWinnerName = (matchData, gameState) => {
    if (!gameState.winner) return 'N/A';
    return gameState.winner === 'player1' ? matchData.player1 : matchData.player2;
};

const getMatchDuration = (gameState) => {
    if (!gameState.startTime || !gameState.endTime) return 'N/A';
    const diffMs = new Date(gameState.endTime) - new Date(gameState.startTime);
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 1) return "< 1 minute";
    if (diffMins === 1) return "1 minute";
    return `${diffMins} minutes`;
};


export const CompletedView = ({ matchData, gameState, shotLog, gameMode, startNewMatch, navigateTo, returnHome }) => {
    if (!matchData || !gameState) {
        return (
            <div className="min-h-screen flex items-center justify-center p-3 text-center">
                <div>
                    <h2 className="text-xl text-slate-400">No match data available.</h2>
                    <button 
                        onClick={returnHome} 
                        className="mt-4 bg-slate-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-slate-700 transition-all flex items-center justify-center"
                    >
                        <Home className="w-5 h-5 mr-2" /> Return to Home
                    </button>
                </div>
            </div>
        );
    }
    
    const winnerName = getWinnerName(matchData, gameState);
    const wasForfeited = gameState.forfeited;

    return (
        <div className="min-h-screen flex items-center justify-center p-3">
            <div className="max-w-xl w-full text-center">
                <div className="bg-yellow-500/20 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4 border-2 border-yellow-500 shadow-lg">
                    <Trophy className="w-12 h-12 text-yellow-400" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold mb-2">
                    {wasForfeited ? 'Match Forfeited' : (gameMode === 'ghost' ? 'Drill Complete!' : 'Match Complete!')}
                </h1>
                <h2 className="text-xl sm:text-2xl font-semibold text-green-400 mb-4">
                    {gameMode === 'pvp' 
                        ? wasForfeited 
                            ? `${winnerName} wins by forfeit.`
                            : `🎉 ${winnerName} Wins! 🎉` 
                        : `You won ${gameState.player1Score} of ${matchData.player1RaceTo} racks.`
                    }
                </h2>

                <div className="bg-slate-800/50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-slate-700">
                    <div className="grid grid-cols-2 gap-4 mb-4 text-2xl sm:text-3xl font-bold">
                        <div className="text-center">
                            <div className="text-md sm:text-lg font-medium truncate">{matchData.player1}</div>
                            <div>{gameState.player1Score}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-md sm:text-lg font-medium truncate">{matchData.player2}</div>
                            <div>{gameState.player2Score}</div>
                        </div>
                    </div>
                    
                    <div className="text-sm text-slate-300 space-y-2 border-t border-slate-700 pt-4">
                        <div className="flex justify-between">
                            <span>Game:</span> 
                            <span className="font-semibold">
                                {matchData.gameType} • {gameMode === 'pvp' 
                                    ? `Race: ${matchData.player1RaceTo} - ${matchData.player2RaceTo}` 
                                    : `Race to ${matchData.player1RaceTo}`
                                }
                            </span>
                        </div>
                        {matchData.location && (
                            <div className="flex justify-between">
                                <span>Location:</span> 
                                <span className="font-semibold">{matchData.location}</span>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <span>Duration:</span> 
                            <span className="font-semibold">{getMatchDuration(gameState)}</span>
                        </div>
                        {matchData.trackingMode !== 'simple' && (
                             <div className="flex justify-between">
                                <span>Total Shots:</span> 
                                <span className="font-semibold">{shotLog.length}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-3">
                    <button 
                        onClick={startNewMatch}
                        className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold text-lg hover:bg-green-700 transition-all flex items-center justify-center shadow-lg"
                    >
                        <RotateCcw className="w-5 h-5 mr-2" /> Start New Match
                    </button>
                    {matchData.trackingMode !== 'simple' && (
                        <button 
                            onClick={() => navigateTo('analytics')}
                            className="w-full bg-purple-600 text-white py-2.5 rounded-xl font-medium hover:bg-purple-700 transition-all flex items-center justify-center"
                        >
                            <BarChart2 className="w-5 h-5 mr-2" /> View Final Analytics
                        </button>
                    )}
                    <button 
                        onClick={returnHome}
                        className="w-full bg-slate-600 text-white py-2.5 rounded-xl font-medium hover:bg-slate-700 transition-all flex items-center justify-center"
                    >
                        <Home className="w-5 h-5 mr-2" /> Return to Home
                    </button>
                </div>
            </div>
        </div>
    );
};